import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, type OrderEvent, type OrderRow, type OrderStatus } from "@/lib/api";
import { FONTS, getItem, formatPrice, gridLimitsFor } from "@/lib/donation-items";

const STATUSES: OrderStatus[] = ["pending", "acknowledged", "paid", "fulfilled", "cancelled"];

function parseLines(json: string | null): string[] {
  if (!json) return [];
  try {
    const v = JSON.parse(json);
    if (Array.isArray(v)) return v;
  } catch {}
  return [];
}

export default function AdminOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const orderId = Number(id);
  const [order, setOrder] = useState<OrderRow | null>(null);
  const [events, setEvents] = useState<OrderEvent[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  async function load() {
    try {
      const r = await api.getOrder(orderId);
      setOrder(r.order);
      setEvents(r.events);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load");
    }
  }

  useEffect(() => {
    if (Number.isFinite(orderId) && orderId > 0) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  async function onArchive(archived: boolean) {
    if (archived && !confirm("Archive this donation? It will be hidden from the normal list but can be restored from the Archived filter.")) return;
    setBusy(true); setErr(null);
    try {
      await api.archiveOrder(orderId, archived);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not update");
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    if (!order) return;
    if (!confirm(`Permanently delete donation #${order.id} (${order.purchaser_name})? This removes it and its history for good and cannot be undone.`)) return;
    setBusy(true); setErr(null);
    try {
      await api.deleteOrder(orderId);
      navigate("/admin/donations", { replace: true });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not delete");
      setBusy(false);
    }
  }

  if (!order) return <div className="text-museum-muted">Loading…</div>;

  const item = getItem(order.item_type);
  const archived = !!order.archived_at;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between text-sm">
        <button onClick={() => navigate(-1)} className="underline">← Back</button>
        {!editing && <button className="btn-secondary" onClick={() => setEditing(true)}>Edit donation</button>}
      </div>

      {editing ? (
        <EditForm order={order} onCancel={() => setEditing(false)} onSaved={async () => { setEditing(false); await load(); }} />
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="card p-5">
              <div className="flex justify-between items-start gap-3">
                <div>
                  <h1 className="text-xl font-serif">Donation #{order.id}</h1>
                  <div className="text-sm text-museum-muted">
                    Submitted {new Date(order.created_at + "Z").toLocaleString()}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 justify-end">
                  {archived && <Pill className="bg-stone-200 text-stone-700">Archived</Pill>}
                  <StatusBadge status={order.status} />
                </div>
              </div>
            </div>

            <Section title="Item">
              <KV label="Item" value={item?.name ?? order.item_type} />
              <KV label="Contribution" value={formatPrice(order.total_cents)} />
              {item?.supportsLogo ? <KV label="Logo" value={order.with_logo ? "With IBEW logo" : "Without logo"} /> : null}
              <KV label="Local #" value={order.local_number} />
              {item?.supportsFont ? <KV label="Font" value={FONTS.find((f) => f.id === order.font)?.label ?? order.font} /> : null}
              {order.custom_logo_requested ? <KV label="Custom logo" value="Yes — donor to send .eps / .ai" /> : null}
            </Section>

            <Section title="Engraving">
              <div className="sm:col-span-2">
                <EngravingView order={order} />
              </div>
            </Section>

            <Section title="Purchaser">
              <KV label="Name" value={order.purchaser_name} />
              <KV label="Email" value={<a className="underline" href={`mailto:${order.contact_email}`}>{order.contact_email}</a>} />
              <KV label="Phone" value={order.contact_phone} />
              <KV label="Address" value={`${order.street}, ${order.city}, ${order.state} ${order.zip}`} />
              <KV label="IBEW affiliation" value={order.ibew_affiliation} />
              <KV label="Internal notes" value={order.notes} block />
            </Section>
          </div>

          <div className="space-y-4">
            <div className="card p-5 space-y-2">
              <h2 className="font-serif text-lg">Manage</h2>
              <p className="help">
                {archived
                  ? "This donation is archived — hidden from the normal list. Restore it to bring it back."
                  : "Archiving hides this donation from the normal list. It stays in the database and can be restored anytime."}
              </p>
              {archived ? (
                <button className="btn-secondary w-full" disabled={busy} onClick={() => onArchive(false)}>
                  {busy ? "Working…" : "Restore from archive"}
                </button>
              ) : (
                <button className="btn-secondary w-full" disabled={busy} onClick={() => onArchive(true)}>
                  {busy ? "Working…" : "Archive donation"}
                </button>
              )}
              {archived && order.archived_at && (
                <div className="text-xs text-museum-muted">
                  Archived {new Date(order.archived_at).toLocaleString()}{order.archived_by ? ` by ${order.archived_by}` : ""}
                </div>
              )}

              <div className="pt-2 mt-2 border-t border-stone-200">
                <p className="help">Permanent delete removes the donation and its history for good. Use for test / junk entries only.</p>
                <button
                  className="w-full mt-1 px-3 py-2 rounded border border-amber-400 text-amber-800 bg-amber-50 hover:bg-amber-100 text-sm font-medium disabled:opacity-50"
                  disabled={busy}
                  onClick={onDelete}
                >
                  Delete permanently
                </button>
              </div>
            </div>

            <div className="card p-5">
              <h2 className="font-serif text-lg mb-2">Activity</h2>
              <ol className="space-y-2 text-sm">
                {events.map((e) => (
                  <li key={e.id}>
                    <div className="text-xs text-museum-muted">
                      {new Date(e.created_at + "Z").toLocaleString()} · {e.actor_email ?? "system"}
                    </div>
                    <div><strong>{e.action}</strong>{e.detail ? `: ${e.detail}` : ""}</div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      )}

      {err && <div className="mt-4 bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded">{err}</div>}
    </div>
  );
}

function EngravingView({ order }: { order: OrderRow }) {
  const item = getItem(order.item_type);
  if (item?.engraving === "grid") {
    const lines = parseLines(order.engraving_lines).filter((l) => l.trim() !== "");
    if (!lines.length) return <span className="text-museum-muted">(no engraving text)</span>;
    return <pre className="font-mono text-sm bg-stone-50 border border-stone-200 rounded p-3 whitespace-pre-wrap">{lines.join("\n")}</pre>;
  }
  if (item?.engraving === "design") {
    return <div className="whitespace-pre-wrap text-sm">{order.design_description || <span className="text-museum-muted">(no design provided)</span>}</div>;
  }
  if (item?.engraving === "name") {
    return <div className="text-sm font-medium">{order.plaque_name || <span className="text-museum-muted">(no name provided)</span>}</div>;
  }
  return <span className="text-museum-muted">—</span>;
}

// ---- Edit form ----

function EditForm({ order, onSaved, onCancel }: { order: OrderRow; onSaved: () => void; onCancel: () => void }) {
  const item = getItem(order.item_type);
  const [purchaserName, setPurchaserName] = useState(order.purchaser_name);
  const [street, setStreet] = useState(order.street);
  const [city, setCity] = useState(order.city);
  const [stateV, setStateV] = useState(order.state);
  const [zip, setZip] = useState(order.zip);
  const [phone, setPhone] = useState(order.contact_phone);
  const [email, setEmail] = useState(order.contact_email);
  const [ibew, setIbew] = useState(order.ibew_affiliation ?? "");
  const [withLogo, setWithLogo] = useState<boolean>(!!order.with_logo);
  const [localNumber, setLocalNumber] = useState(order.local_number ?? "");
  const [font, setFont] = useState(order.font ?? "");
  const [customLogo, setCustomLogo] = useState<boolean>(!!order.custom_logo_requested);
  const [status, setStatus] = useState<OrderStatus>(order.status);
  const [notes, setNotes] = useState(order.notes ?? "");
  const [gridText, setGridText] = useState(parseLines(order.engraving_lines).join("\n"));
  const [design, setDesign] = useState(order.design_description ?? "");
  const [plaque, setPlaque] = useState(order.plaque_name ?? "");

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const limits = item ? gridLimitsFor(item, withLogo) : null;

  async function save() {
    setBusy(true); setErr(null);
    try {
      const body: Record<string, unknown> = {
        purchaser_name: purchaserName,
        street, city, state: stateV, zip,
        contact_phone: phone,
        contact_email: email,
        ibew_affiliation: ibew,
        with_logo: withLogo,
        local_number: localNumber,
        font,
        custom_logo_requested: customLogo,
        status,
        notes,
      };
      if (item?.engraving === "grid") {
        body.engraving_lines = gridText.split(/\r?\n/).map((l) => l.replace(/\s+$/, ""));
      } else if (item?.engraving === "design") {
        body.design_description = design;
      } else if (item?.engraving === "name") {
        body.plaque_name = plaque;
      }
      await api.editOrder(order.id, body);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not save changes");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card p-5 space-y-5 max-w-3xl">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-serif">Edit donation #{order.id}</h1>
        <button className="text-sm text-museum-muted underline" onClick={onCancel}>Cancel</button>
      </div>

      <div className="text-sm text-museum-muted">
        {item?.name ?? order.item_type} · {formatPrice(order.total_cents)} <span className="italic">(item and price are fixed)</span>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Purchaser name"><input className="input" value={purchaserName} onChange={(e) => setPurchaserName(e.target.value)} /></Field>
        <Field label="Contact email"><input className="input" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label="Contact phone"><input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} /></Field>
        <Field label="IBEW affiliation"><input className="input" value={ibew} onChange={(e) => setIbew(e.target.value)} /></Field>
        <Field label="Street"><input className="input" value={street} onChange={(e) => setStreet(e.target.value)} /></Field>
        <Field label="City"><input className="input" value={city} onChange={(e) => setCity(e.target.value)} /></Field>
        <Field label="State"><input className="input" value={stateV} onChange={(e) => setStateV(e.target.value)} /></Field>
        <Field label="ZIP"><input className="input" value={zip} onChange={(e) => setZip(e.target.value)} /></Field>
        <Field label="Status">
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value as OrderStatus)}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        {item?.supportsFont && (
          <Field label="Font">
            <select className="select" value={font} onChange={(e) => setFont(e.target.value)}>
              <option value="">—</option>
              {FONTS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </Field>
        )}
        {item?.supportsLogo && (
          <Field label="Local # (with logo)"><input className="input" value={localNumber} onChange={(e) => setLocalNumber(e.target.value)} /></Field>
        )}
      </div>

      {item?.supportsLogo && (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={withLogo} onChange={(e) => setWithLogo(e.target.checked)} /> Include IBEW logo
        </label>
      )}
      {item?.supportsCustomLogo && (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={customLogo} onChange={(e) => setCustomLogo(e.target.checked)} /> Custom logo (donor sending .eps / .ai)
        </label>
      )}

      {item?.engraving === "grid" && (
        <Field label={`Engraving lines${limits ? ` (max ${limits.lines} lines × ${limits.charsPerLine} chars)` : ""}`} wide>
          <textarea className="textarea font-mono" rows={6} value={gridText} onChange={(e) => setGridText(e.target.value)} />
        </Field>
      )}
      {item?.engraving === "design" && (
        <Field label="Design description" wide><textarea className="textarea" rows={5} value={design} onChange={(e) => setDesign(e.target.value)} /></Field>
      )}
      {item?.engraving === "name" && (
        <Field label="Plaque name" wide><input className="input" value={plaque} onChange={(e) => setPlaque(e.target.value)} /></Field>
      )}

      <Field label="Internal notes (staff-only)" wide><textarea className="textarea" value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>

      {err && <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded text-sm">{err}</div>}

      <div className="flex gap-2">
        <button className="btn-primary" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save changes"}</button>
        <button className="btn-ghost" disabled={busy} onClick={onCancel}>Cancel</button>
      </div>
      <p className="help">Editing changes the stored order only — it does not email the donor.</p>
    </div>
  );
}

function Field({ label, wide, children }: { label: string; wide?: boolean; children: React.ReactNode }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card p-5">
      <h2 className="font-serif text-lg mb-2">{title}</h2>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">{children}</div>
    </div>
  );
}

function KV({ label, value, block }: { label: string; value: React.ReactNode; block?: boolean }) {
  if (value == null || value === "") return null;
  return (
    <div className={block ? "sm:col-span-2" : ""}>
      <div className="text-xs uppercase tracking-wide text-museum-muted">{label}</div>
      <div className="whitespace-pre-wrap">{value}</div>
    </div>
  );
}

function Pill({ className, children }: { className: string; children: React.ReactNode }) {
  return <span className={`inline-block px-2 py-1 rounded text-xs ${className}`}>{children}</span>;
}

function StatusBadge({ status }: { status: OrderStatus }) {
  const cls: Record<OrderStatus, string> = {
    pending:      "bg-amber-100 text-amber-800",
    acknowledged: "bg-sky-100 text-sky-800",
    paid:         "bg-green-100 text-green-800",
    fulfilled:    "bg-emerald-100 text-emerald-800",
    cancelled:    "bg-stone-200 text-stone-700",
  };
  return <span className={`inline-block px-2 py-1 rounded text-xs ${cls[status]}`}>{status}</span>;
}
